import config from "../../common/Config";
import {
  encryptPayload,
  normalizeEncryptedApiResponse,
} from "../../../utils/customerCrypto";
import {
  getCompanySettings,
  resolveCustomerApiEncrypt,
} from "../../../utils/companySettingsCache";

export const PRODUCT_FORM_FIELD_ALIASES = {
  product_code: "code",
  product_name: "name",
  product_details: "details",
  product_type: "split",
  unit_id: "unit",
  vat_id: "tax_rate",
  brand_id: "brand",
  category_id: "category",
  min_qty: "min_alert_quantity",
  max_qty: "max_alert_quantity",
  maximum_order_qty: "maximum_order_quantity",
  inner_ean: "inner_ean_number",
  outer_ean: "outer_ean_number",
  pallet_ean: "pallete_ean",
  discount_product: "is_discount_allow",
  available_for: "availableFor",
  box_cost: "cost",
  recommended_selling_price: "recommendedSellingBoxPrice",
  piece_recommended_selling_price: "recommendedSellingPiecePrice",
  box_margin: "margin",
};

export const PRODUCT_TYPE_TO_SPLIT = {
  box: "0",
  piece: "1",
  both: "2",
};

export const SPLIT_TO_PRODUCT_TYPE = {
  "0": "box",
  "1": "piece",
  "2": "both",
};

export const resolveProductFormFieldName = (apiName) =>
  PRODUCT_FORM_FIELD_ALIASES[apiName] || apiName;

export const isSectionVisible = (section, formData = {}) => {
  const rule = section?.visibleWhen;
  if (!rule || !rule.field) {
    return true;
  }
  const formKey = resolveProductFormFieldName(rule.field);
  let current = formData[formKey];
  if (formKey === "split") {
    current = SPLIT_TO_PRODUCT_TYPE[String(current)] ?? current;
  }
  const values = Array.isArray(rule.values) ? rule.values : [];
  return values.map(String).includes(String(current));
};

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const normalizeOption = (option, field = {}) => {
  if (option == null) return null;
  if (typeof option !== "object") {
    return { label: String(option), value: String(option) };
  }
  const valueKey = field.optionValue || "value";
  const labelKey = field.optionLabel || "label";
  const value = option[valueKey] ?? option.value ?? option.id ?? option.key ?? "";
  const label =
    option[labelKey] ?? option.label ?? option.name ?? option.title ?? value;
  return { label: String(label), value: String(value) };
};

const normalizeProductTypeOptions = (options) =>
  options.map((option) => {
    const value = String(option.value ?? "").toLowerCase();
    return {
      ...option,
      value: PRODUCT_TYPE_TO_SPLIT[value] ?? option.value,
    };
  });

const normalizeUnitOptions = (options) =>
  options.map((option) => ({
    ...option,
    value: String(option.value ?? "").toLowerCase(),
    label: String(option.label ?? option.value ?? "").toUpperCase(),
  }));

const normalizeField = (field = {}) => {
  const name = String(field.name || field.field_name || field.key || "").trim();
  let options = asArray(field.options || field.values || field.choices)
    .map((option) => normalizeOption(option, field))
    .filter(Boolean);

  if (name === "product_type") {
    options = normalizeProductTypeOptions(options);
  }
  if (name === "length_unit" || name === "width_unit" || name === "height_unit") {
    options = normalizeUnitOptions(options);
  }

  let defaultValue =
    field.default ?? field.defaultValue ?? field.default_value ?? "";
  if (name === "product_type") {
    const key = String(defaultValue || "box").toLowerCase();
    defaultValue = PRODUCT_TYPE_TO_SPLIT[key] ?? defaultValue;
  }
  if (name === "length_unit" || name === "width_unit" || name === "height_unit") {
    defaultValue = String(defaultValue || "mm").toLowerCase();
  }
  if (typeof defaultValue === "boolean") {
    defaultValue = defaultValue ? "1" : "0";
  }
  if (name === "available_for" || name === "availableFor") {
    if (String(defaultValue).toLowerCase() === "all") {
      defaultValue = "0";
    }
  }

  const spanColumns = Number(field.columns || 0);
  const column = Number(field.column || field.col || 0) || (spanColumns >= 2 ? 0 : 1);

  return {
    name,
    label: field.label || field.field_label || field.title || "",
    type: String(field.type || field.field_type || "text").toLowerCase(),
    required: field.required === true || field.required === 1 || field.required === "1",
    placeholder: field.placeholder || field.place_holder || "",
    column,
    spanColumns,
    options,
    optionsSource: field.optionsSource || field.options_source || "",
    optionValue: field.optionValue || "value",
    optionLabel: field.optionLabel || "label",
    unitField: field.unitField || field.unit_field || "",
    defaultValue,
    visible: field.visible !== false && field.visible !== 0 && field.visible !== "0",
    readOnly: field.readOnly === true || field.readonly === true || field.read_only === true,
    maxLength: field.maxLength ?? field.max_length ?? field.maxlength ?? null,
    min: field.min,
    max: field.max,
    step: field.step,
    accept: asArray(field.accept),
    multiple: field.multiple === true,
    fields: asArray(
      (field.fields && field.fields.length ? field.fields : field.sub_fields) ||
        field.subFields
    ).map(normalizeField),
    minItems: field.minItems,
    maxItems: field.maxItems,
    emptyMessage: field.emptyMessage || "",
  };
};

const pickFirst = (...values) => {
  for (let i = 0; i < values.length; i += 1) {
    const value = values[i];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }
  return "";
};

export const normalizeDynamicFormResponse = (raw) => {
  const root =
    raw?.form && typeof raw.form === "object"
      ? raw.form
      : raw?.data?.form && typeof raw.data.form === "object"
        ? raw.data.form
        : raw?.data && typeof raw.data === "object"
          ? raw.data
          : raw ?? {};

  const sections = asArray(
    root.sections || root.form_sections || root.groups || []
  ).map((section, index) => ({
    id: section.id ?? section.key ?? `section_${index}`,
    title: section.title || section.label || "",
    layout: section.layout || {},
    visibleWhen: section.visibleWhen || section.visible_when || null,
    fields: asArray(section.fields || section.form_fields || [])
      .map(normalizeField)
      .filter((field) => field.name),
  }));

  let fields = asArray(
    root.fields || root.form_fields || root.formFields || []
  )
    .map(normalizeField)
    .filter((field) => field.name);

  if (!fields.length && sections.length) {
    fields = sections.flatMap((section) => section.fields);
  }

  return {
    id: root.id || "",
    title: root.title || root.form_title || "",
    submitLabel: root.submitLabel || root.submit_label || "",
    submitEndpoint: root.submitEndpoint || root.submit_endpoint || "",
    method: root.method || "POST",
    sections,
    fields,
    pricing_type: pickFirst(
      root.pricing_type,
      root.pricingType,
      raw?.pricing_type,
      raw?.pricingType,
      raw?.data?.pricing_type,
      raw?.data?.pricingType,
      raw?.data?.form?.pricing_type,
    ),
    product_type: pickFirst(
      root.product_type,
      root.productType,
      raw?.product_type,
      raw?.productType,
      raw?.data?.product_type,
      raw?.data?.productType,
      raw?.data?.form?.product_type,
    ),
    split: pickFirst(
      root.split,
      raw?.split,
      raw?.data?.split,
      raw?.data?.form?.split,
    ),
  };
};

export const resolveOptionsSourceKey = (optionsSource = "") => {
  const raw = String(optionsSource || "").toLowerCase();
  if (!raw) return "";
  const segmentMatch = raw.match(/form-options\/(?:product|customer)\/([^/?#]+)/i);
  const segment = segmentMatch ? segmentMatch[1] : raw.split("/").filter(Boolean).pop() || raw;
  if (segment.includes("unit") || raw.includes("unit")) return "units";
  if (segment.includes("vat") || raw.includes("vat")) return "vat";
  if (segment.includes("brand") || raw.includes("brand")) return "brands";
  if (segment.includes("categor") || raw.includes("categor")) return "categories";
  if (segment.includes("supplier") || raw.includes("supplier")) return "suppliers";
  if (segment.includes("availab") || raw.includes("availab")) return "availability";
  if (segment.includes("cess") || raw.includes("cess")) return "cess";
  return segment || raw;
};

export const extractFormOptionsSegment = (optionsSource = "") => {
  const raw = String(optionsSource || "").trim();
  if (!raw) return "";
  const match = raw.match(/form-options\/(?:product|customer)\/([^/?#]+)/i);
  if (match?.[1]) return match[1];
  const parts = raw.replace(/^\/+|\/+$/g, "").split("/");
  return parts[parts.length - 1] || "";
};

const normalizeFormOptionItem = (option) => {
  if (option == null) return null;
  if (typeof option !== "object") {
    return { label: String(option), value: String(option) };
  }
  const value = option.value ?? option.id ?? option.key ?? option.code ?? "";
  const label =
    option.label ??
    option.name ??
    option.title ??
    option.unit_name ??
    option.brand_name ??
    option.category_name ??
    option.tax_name ??
    value;
  if (value === "" && label === "") return null;
  return { ...option, label: String(label), value: String(value) };
};

export const normalizeFormOptionsResponse = (raw) => {
  const root = raw?.data ?? raw?.options ?? raw?.items ?? raw?.results ?? raw ?? {};
  const list = Array.isArray(root)
    ? root
    : Array.isArray(root.data)
      ? root.data
      : Array.isArray(root.options)
        ? root.options
        : Array.isArray(root.items)
          ? root.items
          : [];
  return list.map(normalizeFormOptionItem).filter(Boolean);
};

const PRODUCT_OPTIONS_ENDPOINT_BY_SEGMENT = {
  units: "get_unit",
  unit: "get_unit",
  vat: "get_vat",
  brands: "get_brands",
  brand: "get_brands",
  categories: "get_categories",
  category: "get_categories",
  suppliers: "purchases/getSupplier",
  supplier: "purchases/getSupplier",
  cess: "get_cess",
};

const STATIC_PRODUCT_OPTIONS_BY_SEGMENT = {
  availability: [
    { label: "All", value: "0" },
    { label: "TeleCaller", value: "1" },
  ],
  available_for: [
    { label: "All", value: "0" },
    { label: "TeleCaller", value: "1" },
  ],
  availablefor: [
    { label: "All", value: "0" },
    { label: "TeleCaller", value: "1" },
  ],
};

const mapProductOptionsList = (segment, list = []) => {
  const key = String(segment || "").toLowerCase();
  return (list || [])
    .map((item) => {
      if (item == null) return null;
      if (typeof item !== "object") {
        return { label: String(item), value: String(item) };
      }
      let label = item.label ?? item.name ?? item.title ?? "";
      let value = item.value ?? item.id ?? item.key ?? item.code ?? "";
      if (key.includes("unit")) {
        label = item.code ?? item.unit_name ?? item.name ?? label;
        value = item.id ?? item.value ?? value;
      } else if (key.includes("brand")) {
        label = item.name ?? item.brand_name ?? label;
        value = item.id ?? item.value ?? value;
      } else if (key.includes("categor")) {
        label = item.name ?? item.category_name ?? label;
        value = item.id ?? item.value ?? value;
      } else if (key.includes("vat") || key.includes("cess")) {
        label = item.name ?? item.tax_name ?? label;
        value = item.id ?? item.value ?? value;
      } else if (key.includes("supplier")) {
        label = item.name ?? item.supplier_name ?? item.company_name ?? label;
        value = item.id ?? item.value ?? value;
      }
      if (value === "" && label === "") return null;
      return { ...item, label: String(label || value), value: String(value) };
    })
    .filter(Boolean);
};

export const fetchProductFormOptions = async ({
  segment,
  authToken,
  method = "POST",
} = {}) => {
  const token =
    authToken ||
    localStorage.getItem("token") ||
    localStorage.getItem("accessToken");
  if (!segment) return [];

  const segmentKey = String(segment || "").trim().toLowerCase();
  if (STATIC_PRODUCT_OPTIONS_BY_SEGMENT[segmentKey]) {
    return STATIC_PRODUCT_OPTIONS_BY_SEGMENT[segmentKey];
  }

  const endpoint =
    PRODUCT_OPTIONS_ENDPOINT_BY_SEGMENT[segmentKey] ||
    PRODUCT_OPTIONS_ENDPOINT_BY_SEGMENT[resolveOptionsSourceKey(segmentKey)];

  if (!endpoint) {
    return [];
  }

  const settings = await getCompanySettings();
  const useEncrypt = resolveCustomerApiEncrypt(settings);
  const headers = { Authorization: `Bearer ${token}` };
  const url = `${config.API_URL}/${endpoint}`;

  let response;
  if (String(method).toUpperCase() === "GET") {
    response = await fetch(url, { method: "GET", headers });
  } else {
    let body;
    if (useEncrypt) {
      const encryptedPayload = encryptPayload({ segment, module: "product" });
      const formData = new FormData();
      if (encryptedPayload) formData.append("encrypted_payload", encryptedPayload);
      body = formData;
    } else {
      body = new FormData();
    }
    response = await fetch(url, { method: "POST", headers, body });
  }

  const json = await response.json().catch(() => ({}));
  const resolved = normalizeEncryptedApiResponse(json, useEncrypt);
  if (!response.ok && resolved?.success !== true) {
    throw new Error(resolved?.message || `Failed to load product options: ${segment}`);
  }
  const list = normalizeFormOptionsResponse(resolved);
  if (list.length) {
    return mapProductOptionsList(segmentKey, list);
  }
  const rawList = Array.isArray(resolved?.data) ? resolved.data : [];
  return mapProductOptionsList(segmentKey, rawList);
};

export const collectFormOptionsSources = (fields = []) => {
  const sources = new Set();
  const walk = (list) => {
    (list || []).forEach((field) => {
      if (field?.optionsSource) sources.add(String(field.optionsSource));
      if (field?.fields?.length) walk(field.fields);
    });
  };
  walk(fields);
  return [...sources];
};

const LEGACY_OPTION_SEGMENTS = new Set([
  "units",
  "unit",
  "vat",
  "brands",
  "brand",
  "categories",
  "category",
  "suppliers",
  "supplier",
  "cess",
]);

export const loadProductFormOptionsMap = async ({
  fields = [],
  authToken,
} = {}) => {
  const sources = collectFormOptionsSources(fields);
  const map = {};
  await Promise.all(
    sources.map(async (source) => {
      const segment = extractFormOptionsSegment(source);
      if (!segment) return;
      const key = resolveOptionsSourceKey(source);
      if (
        LEGACY_OPTION_SEGMENTS.has(String(segment).toLowerCase()) ||
        LEGACY_OPTION_SEGMENTS.has(String(key).toLowerCase())
      ) {
        return;
      }
      try {
        const options = await fetchProductFormOptions({ segment, authToken });
        map[key] = options;
        map[segment] = options;
        map[source] = options;
        map[`/api/form-options/product/${segment}`] = options;
        map[`form-options/product/${segment}`] = options;
      } catch {
      }
    })
  );
  return map;
};

export const fetchDynamicForm = async ({
  formKey = "add_product",
  authToken,
} = {}) => {
  const token =
    authToken ||
    localStorage.getItem("token") ||
    localStorage.getItem("accessToken");
  const settings = await getCompanySettings();
  const useEncrypt = resolveCustomerApiEncrypt(settings);
  const headers = {
    Authorization: `Bearer ${token}`,
    Accept: "application/json",
  };
  const params = new URLSearchParams({
    form: formKey,
    form_name: formKey,
    form_key: formKey,
    module: "product",
    type: formKey,
  });

  const response = await fetch(
    `${config.API_URL}/get_dynamic_form?${params.toString()}`,
    {
      method: "GET",
      headers,
    },
  );
  const json = await response.json().catch(() => ({}));
  const resolved = normalizeEncryptedApiResponse(json, useEncrypt);
  if (!response.ok && resolved?.success !== true) {
    throw new Error(resolved?.message || "Failed to load dynamic form");
  }
  return normalizeDynamicFormResponse(resolved);
};
